import { useQuery } from '@apollo/client';
import { track } from '@bnnvara/analytics/src/track';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import React from 'react';

import { FetchBrands } from '../../api/queries/brand.gql';
import useLink from './hooks/useLink';

const Link = ({
  href,
  passHref,
  scroll,
  children,
  category,
  action,
  label,
  disableNextLink,
  className,
}) => {
  const { createHref } = useLink();
  const { data, loading } = useQuery(FetchBrands);

  if (loading) {
    return null;
  }

  const brandSlugs = data?.brands?.map(({ slug }) => slug);

  if (disableNextLink) {
    return (
      <a
        href={href}
        onClick={() => {
          track(category, action, label);
        }}
        className={`${className} external-link`}
      >
        {children}
      </a>
    );
  }

  try {
    const url = new URL(href);
    const anchorProps = !url.hostname.includes('bnnvara.nl')
      ? {
          target: '_blank',
          rel: 'noopener noreferrer',
        }
      : {};

    return (
      <a
        href={href}
        {...anchorProps}
        onClick={() => {
          track(category, action, label);
        }}
        className={`${className} external-link`}
      >
        {children}
      </a>
    );
  } catch (error) {
    const isValidElement = React.isValidElement(children);
    const hasAnchorRootElement =
      isValidElement && (children.type === 'a' || children.type.target === 'a');

    const { href: hrefObject, as } = createHref(href, brandSlugs);

    return (
      <NextLink href={hrefObject} as={as} scroll={scroll} passHref={passHref}>
        {hasAnchorRootElement ? (
          children
        ) : (
          // Needed because NextLink only works if child is an <a>, not a button.
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <a
            role="link"
            tabIndex="0"
            onClick={() => {
              track(category, action, label);
            }}
            onKeyDown={() => {
              track(category, action, label);
            }}
            className={className}
          >
            {children}
          </a>
        )}
      </NextLink>
    );
  }
};

Link.defaultProps = {
  passHref: true,
  scroll: false,
  category: null,
  action: null,
  label: null,
  disableNextLink: false,
  className: '',
};

Link.propTypes = {
  href: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      query: PropTypes.shape({}),
    }),
  ]).isRequired,
  passHref: PropTypes.bool,
  scroll: PropTypes.bool,
  children: PropTypes.node.isRequired,
  category: PropTypes.string,
  action: PropTypes.string,
  label: PropTypes.string,
  disableNextLink: PropTypes.bool,
  className: PropTypes.string,
};

export default Link;
